/**
 * Writing-history PDF as a native PDF book (#291).
 * Landscape US Letter so each event can sit on one wrapping line.
 * The compositor paginates. There is no HTML capture.
 */
import {
  isJust,
  isNothing,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "./maybe";
import { trimmedMaybe } from "./trimmedString";
import {
  type ChronicleEntry,
  chronicleSummary,
  formatChronicleWhen,
  formatWordsDiff,
  platformLabel,
  resolveEntryPathLabels,
} from "./chronicle";
import type { PdfBook, PdfBookBlock, PdfBookMark } from "../export/pdfBook";
import type { ZwChapter } from "./zwConfig";

const PAGE_WIDTH_PT = 11 * 72;
const PAGE_HEIGHT_PT = 8.5 * 72;
const MARGIN_PT = 40;

const PLAIN: PdfBookMark = {
  bold: false,
  italic: false,
  smallCaps: false,
  opening: "none",
};

function paragraph(text: string): PdfBookBlock {
  return {
    type: "paragraph",
    firstLineIndent: false,
    runs: [{ text, marks: PLAIN }],
  };
}

function heading(text: string): PdfBookBlock {
  return {
    type: "heading",
    level: 2,
    runs: [{ text, marks: PLAIN }],
  };
}

function textOrDash(value: Maybe<string>): string {
  return matchMaybe(value, {
    just: (text) => text,
    nothing: () => "-",
  });
}

function platformText(entry: ChronicleEntry): string {
  const platform = entry.platform;
  if (platform === "desktop" || platform === "mobile" || platform === "web") {
    return platformLabel(platform);
  }
  return "-";
}

function beaconText(entry: ChronicleEntry): string {
  const pulse = entry.beaconPulse;
  if (typeof pulse !== "number") return "-";
  return `Beacon ${pulse}`;
}

function wordsText(entry: ChronicleEntry): string {
  const words = entry.wordsDiff;
  return `${formatWordsDiff(typeof words === "number" ? words : 0)} words`;
}

/** The embedded book faces have no arrow glyph; rename messages use one. */
function pdfSafeText(text: string): string {
  return text.replace(/\u2192/g, "to");
}

function formatRange(first: Maybe<string>, last: Maybe<string>): string {
  const firstMissing = isNothing(first) || first.value.length === 0;
  const lastMissing = isNothing(last) || last.value.length === 0;
  if (firstMissing && lastMissing) return "No dated entries";
  if (
    isJust(first) &&
    first.value.length > 0 &&
    isJust(last) &&
    last.value.length > 0 &&
    first.value !== last.value
  ) {
    return `${formatChronicleWhen(first.value)} to ${formatChronicleWhen(last.value)}`;
  }
  const single =
    isJust(first) && first.value.length > 0
      ? first.value
      : isJust(last)
        ? last.value
        : "";
  return formatChronicleWhen(single);
}

function entryLine(
  entry: ChronicleEntry,
  chapters: readonly ZwChapter[],
): string {
  const labels = resolveEntryPathLabels(entry, [...chapters]);
  const files = labels.length === 0 ? "-" : labels.join(", ");
  return [
    formatChronicleWhen(entry.timestamp),
    textOrDash(trimmedMaybe(entry.author)),
    wordsText(entry),
    platformText(entry),
    beaconText(entry),
    files,
    pdfSafeText(textOrDash(trimmedMaybe(entry.message))),
  ].join("  ·  ");
}

export function chroniclePdfDefaultName(projectTitle: string): string {
  const base = matchMaybe(trimmedMaybe(projectTitle), {
    just: (title) => title,
    nothing: () => "Project",
  });
  return `${base} - Chronicle.pdf`;
}

/** Native PDF book for the writing-history export. */
export function buildChroniclePdfBook(
  entries: readonly ChronicleEntry[],
  projectTitle: string,
  chapters: readonly ZwChapter[],
): PdfBook {
  const title = matchMaybe(trimmedMaybe(projectTitle), {
    just: (value) => value,
    nothing: () => "Untitled project",
  });
  const summary = chronicleSummary(entries);
  const newestFirst = [...entries].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp),
  );
  const blocks: PdfBookBlock[] = [
    paragraph(`Entries: ${summary.entryCount}`),
    paragraph(`Net words: ${formatWordsDiff(summary.netWords)}`),
    paragraph(`Files touched: ${summary.fileCount}`),
    paragraph(
      `Range: ${formatRange(toMaybe(summary.firstTimestamp), toMaybe(summary.lastTimestamp))}`,
    ),
    paragraph(
      "A dated log of when you wrote, which scenes changed, and whether the manuscript grew or shrank.",
    ),
    heading("Events"),
  ];
  if (newestFirst.length === 0) {
    blocks.push(paragraph("No writing history entries yet."));
  } else {
    for (const entry of newestFirst) {
      blocks.push(paragraph(entryLine(entry, chapters)));
    }
  }

  return {
    title,
    author: "",
    printSize: "letter",
    page: { widthPt: PAGE_WIDTH_PT, heightPt: PAGE_HEIGHT_PT },
    marginPt: MARGIN_PT,
    fonts: { bodyId: "atkinson", headingId: "literata" },
    pageChrome: {
      showTitle: true,
      showAuthor: false,
      pageNumber: "footer",
    },
    frontMatter: {
      titlePage: { title: "", subtitle: "", author: "" },
      copyright: nothing,
      dedication: nothing,
    },
    body: [
      {
        type: "chapter",
        title: "Writing history",
        heading: {
          alignment: "left",
          sizePt: 16,
          spaceBeforePt: 0,
          spaceAfterPt: 10,
          imagePath: "",
          imagePosition: "above",
          image: nothing,
        },
        blocks,
      },
    ],
  };
}
